import _ from "lodash";
/* eslint-disable react/display-name */
import React, { useState } from "react";

import { getCriteriaValueFromCache } from "modules/CommonGraphqlHelpers";
import { getTitle } from "ClassProgressSummary/modules/Utils";
import { colors, fontWeight } from "Constants/colorConstants";
import I18nHOC from "UIComponents/I18nHOC";
import DialogueBox from "UIComponents/DialogueBox";
import { getRatingHistoryDate } from "AppComponents/ProgressSummaryV2Shared/modules/Utils.js";
import classNames from "classnames";

import { DeleteOutlined } from "@toddle-edu/ds-icons";
import classes from "./HistoryList.scss";
import Thumbnail from "../Thumbnail";

const getHistoryItemStyle = ({ isPopUp, showBasicHistoryList, color }) => {
  if (showBasicHistoryList) {
    return {
      title: {
        fontSize: "1.4rem",
        color: colors.slate,
      },
      subTitle: {
        display: "none",
      },
      rating: {
        fontSize: "1.4rem",
        color: color ? color : "",
        fontWeight: fontWeight.AvenirNextBold,
      },
      itemStyle: {
        padding: "4px 0px",
      },
    };
  } else if (isPopUp) {
    return {
      title: {
        fontSize: "1.2rem",
      },
      subTitle: {
        fontSize: "1.0rem",
      },
      rating: {
        color: color ? color : "",
        fontSize: "1.2rem",
      },
    };
  } else {
    return {
      rating: {
        color: color ? color : "",
      },
    };
  }
};

const getSeeMore = ({ parentType, parent, onClickHistoryItem, t }) => {
  switch (parentType) {
    case "STUDENT_PROGRESS_REPORT":
      return (
        <div
          className={classes.seeMore}
          onClick={() => onClickHistoryItem({ parentType, parent })}
        >
          {t("common:open_with_label", { label: t("common:report_card") })}
        </div>
      );
    case "STUDENT_ASSESSMENT_EVALUATION":
      return (
        <div
          className={classes.seeMore}
          onClick={() => onClickHistoryItem({ parentType, parent })}
        >
          {t("common:open_with_label", {
            label: t("common:assessment_evaluation"),
          })}
        </div>
      );
    case "POST":
      return (
        <div
          className={classes.seeMore}
          onClick={() => onClickHistoryItem({ parentType, parent })}
        >
          {t("common:open_with_label", { label: t("common:portfolio_post") })}
        </div>
      );
    case "STUDENT_ASSIGNMENT":
      return (
        <div
          className={classes.seeMore}
          onClick={() => onClickHistoryItem({ parentType, parent })}
        >
          {t("common:open_with_label", {
            label: t("common:assessment_evaluation"),
          })}
        </div>
      );
    default:
      return null;
  }
};

const deletableTypes = ["OBSERVATION"];

const DIALOG_INFO = {
  OBSERVATION: {
    title: "common:delete_with_label",
    label: "progressSummary:observation",
    message: "progressSummary:are_you_sure_delete_record",
  },
};

const HistoryList = React.memo(props => {
  const {
    ratingHistory,
    onClickHistoryItem,
    t,
    isPopUp,
    /*in basic version of history list, we dont show the thumbnail, subtitle and we show only black color
    for the rating given.*/
    showBasicHistoryList = false,
    canRemoveRating,
    showThumbnail = true,
    deleteRatingHistory,
  } = props;

  const [currentDialogItem, setCurrentDialogItem] = useState(null);

  return (
    <div className={classes.container}>
      {currentDialogItem && (
        <DialogueBox
          modalTitle={t(
            DIALOG_INFO[_.get(currentDialogItem, "parentType")].title,
            {
              label: t(
                DIALOG_INFO[_.get(currentDialogItem, "parentType")].label
              ),
            }
          )}
          showModal={true}
          onClickButton2={() => deleteRatingHistory(currentDialogItem)}
          modalBody={t(
            DIALOG_INFO[_.get(currentDialogItem, "parentType")].message
          )}
          toggleDialogueBoxDisplay={() => setCurrentDialogItem(null)}
          button1={t("common:cancel")}
          button2={t("common:delete")}
        />
      )}
      {!isPopUp && (
        <div className={classes.bodyLabel}>
          {t("progressSummary:evaluation_history")}
        </div>
      )}
      <div className={classes.itemsContainer}>
        {!_.isEmpty(ratingHistory) ? (
          _.map(
            ratingHistory,
            ({
              id,
              parentType,
              parent,
              publishedAt,
              academicCriteriaValue,
              createdBy: { firstName, lastName } = {},
            }) => {
              const { abbreviation, color } = getCriteriaValueFromCache(
                academicCriteriaValue
              );
              const historyItemStyle = getHistoryItemStyle({
                isPopUp,
                showBasicHistoryList,
                color,
              });

              const titleStyle = _.get(historyItemStyle, "title", {});
              const subTitleStyle = _.get(historyItemStyle, "subTitle", {});
              const ratingStyle = _.get(historyItemStyle, "rating", {});
              const itemStyle = _.get(historyItemStyle, "itemStyle", {});
              const isItemHovered =
                canRemoveRating && _.includes(deletableTypes, parentType);
              const itemStyles = classNames(classes.item, {
                [classes.itemHovered]: isItemHovered,
                [classes.itemTopAligned]: !showBasicHistoryList,
              });
              const thumbnailSize = isPopUp ? "24px" : null;
              return (
                <div className={itemStyles} style={itemStyle} key={id}>
                  {showThumbnail && (
                    <Thumbnail
                      parent={parent}
                      displayOn={"list"}
                      thumbnailSize={thumbnailSize}
                      parentType={parentType}
                      isPopUp={isPopUp}
                    />
                  )}

                  <div className={classes.middleItem}>
                    <div className={classes.title} style={titleStyle}>
                      {getTitle({ parent, parentType })}
                    </div>
                    <div className={classes.subTitle} style={subTitleStyle}>
                      {!isPopUp
                        ? t("common:evaluated_by_with_label", {
                            name: `${firstName} ${lastName}`,
                          })
                        : `${firstName} ${lastName}`}
                      {` |  ${getRatingHistoryDate({ publishedAt, t })}`}
                    </div>
                    {!isPopUp &&
                      getSeeMore({
                        parentType,
                        parent,
                        onClickHistoryItem,
                        t,
                      })}
                  </div>

                  <div className={classes.deleteButtonContainer}>
                    <div
                      className={classes.deleteButton}
                      onClick={() =>
                        setCurrentDialogItem({ id, parent, parentType })
                      }
                    >
                      <DeleteOutlined size={"xx-small"} variant={"critical"} />
                    </div>
                  </div>

                  <div className={classes.rating} style={ratingStyle}>
                    {abbreviation || "-"}
                  </div>
                </div>
              );
            }
          )
        ) : (
          <div className={classes.currentRatingEmptyValue}>{`-`}</div>
        )}
      </div>
    </div>
  );
});

export default I18nHOC({ resource: ["common", "progressSummary"] })(
  HistoryList
);
